import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import BaseCard from "../../../components/ui/BaseCard";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import * as yup from "yup";
import { useFormik } from "formik";
import { useMutation } from "@tanstack/react-query";
import { requestMissingDocuments } from "../../../services/api/admin";

const validationSchema = yup.object({
  emailTemplate: yup
    .array()
    .min(1, "At least one template must be selected")
    .test("custom-email-exclusive", "Custom email must be the only selected option", (value) => {
      if (!value) return false;
      return value.includes("CUSTOM_EMAIL") ? value.length === 1 : true;
    }),
  emailContent: yup.string().when("emailTemplate", {
    is: (val) => val.includes("CUSTOM_EMAIL"),
    then: (schema) => schema.required("Custom email content is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const SendEmailToEngineerModal = ({
  setIsSendEmailToEngineerModalOpen,
  isSendEmailToEngineerModalOpen,
  onStatusChange,
  selectedEngineer,
}) => {
  const {
    isPending: isRequestMissingDocumentsApiLoading,
    error: isRequestMissingDocumentsApiError,
    mutateAsync: requestMissingDocumentsMutation,
  } = useMutation({
    mutationFn: requestMissingDocuments,
    enabled: false,
  });
  const formik = useFormik({
    initialValues: {
      emailTemplate: [],
      emailContent: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleConfirmApprovalButton();
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    validateOnMount: true,
  });
  console.log("formik.isValid", formik.isValid);
  const handleConfirmApprovalButton = async () => {
    try {
      console.log("formik", formik.values);
      const response = await requestMissingDocumentsMutation({
        ...formik.values,
        engineerId: selectedEngineer.id,
      });
      // The server decides the resulting status: a PENDING engineer has never
      // submitted, so emailing them only counts a reminder and leaves them
      // PENDING. Hardcoding UNAPPROVE here flashed the wrong chip until the
      // refetch landed. Fall back to the current status if the API is older.
      const nextStatus = response?.data?.status ?? selectedEngineer.status;
      onStatusChange?.(selectedEngineer.id, nextStatus);
      setIsSendEmailToEngineerModalOpen(false);
    } catch (error) {}
  };

  const handleStandardTemplateToggle = (value) => {
    const current = formik.values.emailTemplate;

    if (current.includes("CUSTOM_EMAIL")) {
      // If CUSTOM_EMAIL is selected and another is clicked, remove CUSTOM_EMAIL and add new
      formik.setFieldValue("emailTemplate", [value]);
    } else {
      // Standard toggle behavior
      formik.setFieldValue(
        "emailTemplate",
        current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
      );
    }
  };

  return (
    <BaseModal
      open={isSendEmailToEngineerModalOpen}
      close={() => {
        setIsSendEmailToEngineerModalOpen(false);
        formik.resetForm();
      }}
    >
      <div className="container bg-[#F6F6F6] py-7  xl:w-[700px] lg:w-[900px] sm:w-[700px]">
        <h6
          className="text-dark border-bottom font-normal"
          style={{ marginBottom: "24px", paddingBottom: "4px" }}
        >
          {formik.values.emailTemplate.includes("CUSTOM_EMAIL")
            ? "Email Subject: Important Action Required"
            : formik.values.emailTemplate.length > 0
              ? "Email Subject: Documents Submission Required"
              : "Email"}
        </h6>

        <BaseCard className="p-4 bg-[#EAF1FF] rounded-md" elevation={0}>
          <p className="text-base text-center">
            Please select the email template option to send an email.
          </p>
        </BaseCard>
        <div className="grid grid-cols-4 gap-5">
          {[
            { label: "ID", value: "ID" },
            { label: "Rates", value: "RATES" },
            { label: "Skill Set", value: "SKILL_SET" },
            { label: "Equipment", value: "EQUIPMENT" },
            { label: "Employment", value: "EMPLOYMENT" },
            { label: "Language", value: "LANGUAGE" },
            { label: "Education", value: "EDUCATION" },
            { label: "Profile Picture", value: "PROFILE_PICTURE" },
          ].map((template) => (
            <div
              key={template.value}
              className={`border rounded-md flex py-2 px-3 ${
                formik.values.emailTemplate.includes(template.value) ? "border-primary" : ""
              }`}
            >
              <BaseCheckbox
                label={template.label}
                id="emailTemplate"
                name="emailTemplate"
                checked={formik.values.emailTemplate.includes(template.value)}
                onChange={() => handleStandardTemplateToggle(template.value)}
                onBlur={formik.handleBlur}
                error={formik.touched.emailTemplate && Boolean(formik.errors.emailTemplate)}
                errorMsg={formik.touched.emailTemplate && formik.errors.emailTemplate}
              />
            </div>
          ))}
        </div>
        <hr className="border-t border-gray-300 mt-6" />
        <div className="col-span-3 ml-3 mt-1">
          <BaseCheckbox
            label="Send a custom email"
            id="emailTemplate"
            name="emailTemplate"
            checked={formik.values.emailTemplate.includes("CUSTOM_EMAIL")}
            onChange={() => {
              const current = formik.values.emailTemplate;
              if (current.includes("CUSTOM_EMAIL")) {
                // Uncheck CUSTOM_EMAIL
                formik.setFieldValue(
                  "emailTemplate",
                  current.filter((v) => v !== "CUSTOM_EMAIL"),
                );
              } else {
                formik.setFieldValue("emailTemplate", ["CUSTOM_EMAIL"]);
              }
            }}
            onBlur={formik.handleBlur}
            error={formik.touched.emailTemplate && Boolean(formik.errors.emailTemplate)}
            errorMsg={formik.touched.emailTemplate && formik.errors.emailTemplate}
          />
        </div>
        {formik.values.emailTemplate.includes("CUSTOM_EMAIL") && (
          <textarea
            className="mt-3 border border-zinc-300	w-full rounded-md h-36 resize-none p-4  focus:outline-none focus:ring-1 focus:ring-orange-400 text-xs font-[300]"
            id="emailContent"
            name="emailContent"
            value={formik.values.emailContent}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.emailContent && Boolean(formik.errors.emailContent)}
            helperText={formik.touched.emailContent && formik.errors.emailContent}
          ></textarea>
        )}
      </div>
      <div className="xl:flex grid grid-cols-2 gap-4 xl:justify-center  bg-white py-6 px-4">
        <div className="xl:block col-span-2">
          <BaseButton
            className="py-[6px] px-[40px] bg-light text-dark"
            fullWidth={true}
            onClick={() => {
              setIsSendEmailToEngineerModalOpen(false);
              formik.resetForm();
            }}
          >
            Cancel
          </BaseButton>
        </div>
        <div className="xl:block col-span-2">
          <BaseButton
            loaderSize={24}
            loaderColor="primary"
            disabled={isRequestMissingDocumentsApiLoading || !formik.isValid}
            isLoading={isRequestMissingDocumentsApiLoading}
            className="py-[6px] px-[40px]"
            color="primary"
            fullWidth={true}
            onClick={formik.handleSubmit}
          >
            Confirm
          </BaseButton>
        </div>
      </div>
    </BaseModal>
  );
};

export default SendEmailToEngineerModal;
